import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordDialog = ({ open, onOpenChange, loginType }) => {
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim()) {
      toast.error("Please enter your username");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        username: username.trim(),
        login_type: loginType,
      });
      
      toast.success("Password reset request sent to admin. You will be contacted shortly.");
      setUsername("");
      onOpenChange(false);
    } catch (error) {
      console.error("Forgot password error:", error);
      toast.error(error.response?.data?.detail || "Failed to send reset request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white border border-gray-200 shadow-lg">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2 text-gray-900">
            <Mail className="h-5 w-5 text-amber-500" />
            Forgot Password
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Enter your username and we'll send a password reset request to the administrator.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reset-username" className="font-semibold text-gray-800">
                Username
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  id="reset-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="pl-10 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  disabled={loading}
                  data-testid="forgot-username-input"
                />
              </div>
            </div>
            
            <div className="text-sm bg-amber-50 p-3 rounded-lg border border-amber-200">
              <p className="text-gray-700">
                A password reset notification will be sent to the admin at{" "}
                <strong className="text-amber-700">info@challengepack.in</strong>.
                The admin will contact you with a reset link.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="submit-forgot-password"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Reset Request"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
